import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtMoney } from "@/components/ui-kit";
import { Plus, Pencil, Trash2, Search, Users2 } from "lucide-react";

const TYPE_OPTIONS = [
  { value: "pharmacy", label: "Pharmacy" },
  { value: "clinic", label: "Clinic" },
  { value: "hospital", label: "Hospital" },
  { value: "warehouse", label: "Warehouse" },
  { value: "wholesaler", label: "Wholesaler" },
  { value: "distributor", label: "Distributor" },
  { value: "doctor", label: "Doctor (direct)" },
  { value: "government", label: "Government Entity" },
  { value: "other", label: "Other" },
];

const TYPE_COLOR = {
  pharmacy: "blue", clinic: "purple", hospital: "red",
  warehouse: "amber", wholesaler: "amber", distributor: "green",
  doctor: "purple", government: "slate", other: "slate",
};

const emptyForm = {
  name: "", name_ar: "", customer_type: "pharmacy", contact_person: "",
  phone: "", email: "", tax_registration_number: "", commercial_registration_number: "",
  national_id: "", governorate: "", city: "", area: "", address: "",
  credit_limit: 0, payment_terms: "", notes: "",
};

export default function Customers() {
  const { user } = useAuth();
  const has = (p) => user?.permissions?.includes(p);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ q: "", type: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (filter.q) params.q = filter.q;
    if (filter.type) params.customer_type = filter.type;
    const { data } = await api.get("/customers", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [filter.type]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...emptyForm, ...c });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, credit_limit: parseFloat(form.credit_limit) || 0 };
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      setOpen(false);
      await load();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      await load();
    } catch (err) {
      alert("Cannot delete: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Pharmacies, clinics, hospitals, warehouses, distributors and more"
        actions={
          has("customers.create") && (
            <Btn onClick={openCreate} data-testid="customers-add-btn">
              <Plus className="w-4 h-4" /> New Customer
            </Btn>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search by name, phone or tax number..."
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="ps-9"
              data-testid="customers-search"
            />
          </div>
          <Select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            data-testid="customers-type-filter"
          >
            <option value="">All Types</option>
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
      </Card>

      {items.length === 0 ? (
        <EmptyState message="No customers yet. Click 'New Customer' to add one." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-start">Name</th>
                <th className="px-4 py-3 text-start">Type</th>
                <th className="px-4 py-3 text-start">Tax ID</th>
                <th className="px-4 py-3 text-start">Phone</th>
                <th className="px-4 py-3 text-start">Location</th>
                <th className="px-4 py-3 text-end">Credit Limit</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      <Users2 className="w-4 h-4 text-slate-400" />
                      <div>
                        {c.name}
                        {c.name_ar && <div className="text-xs text-slate-500">{c.name_ar}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><Badge color={TYPE_COLOR[c.customer_type] || "slate"}>{c.customer_type}</Badge></td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.tax_registration_number || "-"}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{c.phone || "-"}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">
                    {[c.city, c.governorate].filter(Boolean).join(", ") || "-"}
                  </td>
                  <td className="px-4 py-3 text-end font-semibold">{fmtMoney(c.credit_limit)} EGP</td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center gap-1">
                      {has("customers.edit") && (
                        <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`customer-edit-${c.id}`}>
                          <Pencil className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </button>
                      )}
                      {has("customers.delete") && (
                        <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40" data-testid={`customer-delete-${c.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Customer" : "New Customer"} maxWidth="max-w-3xl">
        <form onSubmit={submit} className="space-y-4" data-testid="customer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Name (English) *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="customer-name" />
            <Input label="Name (Arabic)" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
            <Select label="Customer Type *" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} data-testid="customer-type">
              {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Input label="Contact Person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input label="Tax Registration # (الرقم الضريبي)" value={form.tax_registration_number} onChange={(e) => setForm({ ...form, tax_registration_number: e.target.value })} />
            <Input label="Commercial Registration # (السجل التجاري)" value={form.commercial_registration_number} onChange={(e) => setForm({ ...form, commercial_registration_number: e.target.value })} />
            <Input label="National ID (for individuals)" value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} />
            <Input label="Governorate" value={form.governorate} onChange={(e) => setForm({ ...form, governorate: e.target.value })} />
            <Input label="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <Input label="Area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} />
            <div className="md:col-span-2">
              <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <Input label="Credit Limit (EGP)" type="number" min="0" step="0.01" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
            <Input label="Payment Terms" placeholder="e.g. Net 30" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
          </div>
          <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="customer-submit">{busy ? "Saving..." : (editing ? "Update" : "Create Customer")}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
